import { FC, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { MiTHEME } from "@miplatform/theme";
import { IWorkOrder, WorkOrderStatus } from "@miplatform/types";
import { formatDate, getContrastColor, isDateAfter } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

interface WorkOrderCardProps {
  workOrder: IWorkOrder;
}

export const WorkOrderCard: FC<WorkOrderCardProps> = memo(({ workOrder }) => {
  const { templateName = "--", status, hardDueDate, completeDate } = workOrder;
  const { classes } = useStyles({ status, hardDueDate, completeDate });
  const { label = "--" } = WORK_ORDER_COLOR_MAP[status];
  const date = useMemo(() => {
    if (completeDate) {
      return `Completed: ${formatDate(
        completeDate,
        DATE_TIME_FORMATS.FORMAT_MM_DD_YY
      )}`;
    }

    if (hardDueDate) {
      return `Due: ${formatDate(
        hardDueDate,
        DATE_TIME_FORMATS.FORMAT_MM_DD_YY
      )}`;
    }

    return "--";
  }, [completeDate, hardDueDate]);

  return (
    <Box className={classes.root}>
      <Typography className={classes.template}>{templateName}</Typography>
      <Typography className={classes.date}>{date}</Typography>
      <Box className={classes.status}>{label}</Box>
    </Box>
  );
});

const useStyles = makeStyles<{
  completeDate?: string;
  hardDueDate?: string;
  status: WorkOrderStatus;
}>()((theme, { completeDate, hardDueDate, status }) => {
  const {
    background,
    border,
    status: statusColors,
  } = WORK_ORDER_COLOR_MAP[status] || WORK_ORDER_COLOR_MAP[WorkOrderStatus.New];

  return {
    root: {
      display: "flex",
      backgroundColor: background,
      border: `0.5px solid ${border}`,
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      gap: theme.spacing(2),
    },
    template: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.semiBold,
    },
    date: {
      fontSize: theme.fontSize.small,
      color: theme.palette.grey.medium,
      flex: 1,
      ...(completeDate &&
        hardDueDate &&
        isDateAfter(completeDate, hardDueDate) && {
          color: theme.palette.error.main,
        }),
    },
    status: {
      backgroundColor: statusColors.background,
      border: `0.5px solid ${statusColors.border}`,
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(0.5, 1),
      fontSize: theme.fontSize.small,
      color: getContrastColor(statusColors.background),
    },
  };
});

const DEFAULT_WORK_ORDER_COLOR = {
  background: MiTHEME.palette.grey.lightAccent,
  border: MiTHEME.palette.grey.darkAccent,
};

const WORK_ORDER_COLOR_MAP = {
  [WorkOrderStatus.New]: {
    ...DEFAULT_WORK_ORDER_COLOR,
    label: "New",
    status: {
      background: MiTHEME.palette.grey.darkAccent,
      border: MiTHEME.palette.grey.medium,
    },
  },
  [WorkOrderStatus.Pending]: {
    ...DEFAULT_WORK_ORDER_COLOR,
    label: "Pending",
    status: {
      background: MiTHEME.palette.blue.main,
      border: MiTHEME.palette.blue.dark,
    },
  },
  [WorkOrderStatus.InProgress]: {
    ...DEFAULT_WORK_ORDER_COLOR,
    label: "In Progress",
    status: {
      background: MiTHEME.palette.blue.main,
      border: MiTHEME.palette.blue.dark,
    },
  },
  [WorkOrderStatus.Hold]: {
    ...DEFAULT_WORK_ORDER_COLOR,
    label: "Hold",
    status: {
      background: MiTHEME.palette.hold.accent,
      border: MiTHEME.palette.blue.main,
    },
  },
  [WorkOrderStatus.Cancelled]: {
    ...DEFAULT_WORK_ORDER_COLOR,
    label: "Cancelled",
    status: {
      background: MiTHEME.palette.error.accent,
      border: MiTHEME.palette.error.main,
    },
  },
  [WorkOrderStatus.Complete]: {
    ...DEFAULT_WORK_ORDER_COLOR,
    label: "Complete",
    status: {
      background: MiTHEME.palette.success.accent,
      border: MiTHEME.palette.success.main,
    },
  },
  [WorkOrderStatus.Late]: {
    background: MiTHEME.palette.error.light,
    border: MiTHEME.palette.error.main,
    label: "Late",
    status: {
      background: MiTHEME.palette.error.accent,
      border: MiTHEME.palette.error.main,
    },
  },
  [WorkOrderStatus.AtRisk]: {
    background: MiTHEME.palette.warning.light,
    border: MiTHEME.palette.warning.main,
    label: "At Risk",
    status: {
      background: MiTHEME.palette.warning.main,
      border: MiTHEME.palette.warning.dark,
    },
  },
};
